import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Animated, Platform, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useTheme, SPACING, BORDER_RADIUS, ELEVATION, TYPOGRAPHY } from '../../theme';
import { useUIStore, useLeftDockInset } from '../../store/uiStore';
import { useAuthStore } from '../../store/authStore';
import { useTabBarClearance } from '../../hooks/useTabBarClearance';
import { useDesktopShell } from '../../hooks/useDesktopShell';
import { TRANSITION_DOCK_INSET } from '../../utils/webStyles';
import { UpgradeAccountSheet } from '../auth/UpgradeAccountSheet';

const AUTO_DISMISS_MS = 8_000;

/**
 * Inline non-blocking nudge for anonymous Fire Scouts who just posted. Sits
 * above the floating tab bar, offers to save their account so their work
 * follows them across devices. The actual signup flow is the existing
 * UpgradeAccountSheet — this is just a low-pressure entry point.
 */
export function SaveAccountToast() {
  const { colors } = useTheme();
  const show = useUIStore((s) => s.showSaveAccountToast);
  const tabBarVisible = useUIStore((s) => s.tabBarVisible);
  const clear = useUIStore((s) => s.clearSaveAccountToast);
  const dismissNudge = useUIStore((s) => s.dismissSaveNudge);
  const hasDismissed = useUIStore((s) => s.hasDismissedSaveNudge);
  const isAnonymous = useAuthStore((s) => s.user?.isAnonymous === true);
  const tabBarClearance = useTabBarClearance();
  // This toast has no isDesktopShell gate (unlike CreatePostCoachmark), so
  // it can render on the desktop shell — where a left-docked defense plan
  // panel would otherwise sit under its left edge.
  const isDesktopShell = useDesktopShell();
  const leftDockInset = useLeftDockInset();
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const opacity = useRef(new Animated.Value(0)).current;

  // The toast renders when (a) the flag was set after a successful post,
  // (b) the user is still anonymous, and (c) they haven't already said
  // "not now". Otherwise we render the upgrade sheet alone so an
  // in-flight upgrade can finish even after the toast disappears.
  const visible = show && isAnonymous && !hasDismissed;

  useEffect(() => {
    if (!visible) return;
    Animated.timing(opacity, {
      toValue: 1,
      duration: 220,
      useNativeDriver: Platform.OS !== 'web',
    }).start();
    const timer = setTimeout(() => {
      // Auto-dismiss is soft — keeps the avatar dot alive so the user can
      // come back to it later via the user menu.
      clear();
    }, AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [visible, opacity, clear]);

  // Reset the fade when the toast hides so the next show starts from 0.
  useEffect(() => {
    if (!visible) opacity.setValue(0);
  }, [visible, opacity]);

  const handleSave = useCallback(() => {
    setUpgradeOpen(true);
    clear();
  }, [clear]);

  const handleNotNow = useCallback(() => {
    dismissNudge();
  }, [dismissNudge]);

  // If a Fire Scout signs in via the upgrade sheet, isAnonymous flips to
  // false on the next auth tick — UpgradeAccountSheet's own onClose handler
  // is all we need to drive it. The sheet stays rendered regardless of
  // toast visibility so the user can complete the form even after the
  // toast auto-dismisses.
  return (
    <>
      {visible && tabBarVisible && (
        <Animated.View
          pointerEvents="box-none"
          style={[
            styles.wrapper,
            { bottom: tabBarClearance, opacity },
            isDesktopShell && TRANSITION_DOCK_INSET,
            isDesktopShell && leftDockInset > 0 && { left: SPACING.md + leftDockInset },
          ]}>
          <View
            style={[
              styles.toast,
              {
                backgroundColor: colors.surfaceElevated,
                borderColor: colors.border,
              },
            ]}>
            <Text style={[styles.message, { color: colors.text }]} numberOfLines={2}>
              Nice post. Save your account so your work follows you?
            </Text>
            <View style={styles.actions}>
              <TouchableOpacity
                onPress={handleNotNow}
                style={styles.secondaryAction}
                accessibilityRole="button"
                accessibilityLabel="Not now">
                <Text style={[styles.secondaryActionText, { color: colors.textSecondary }]}>
                  Not now
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={handleSave}
                style={[styles.primaryAction, { backgroundColor: colors.action }]}
                accessibilityRole="button"
                accessibilityLabel="Save your account">
                <Text style={[styles.primaryActionText, { color: colors.actionOn }]}>Save</Text>
              </TouchableOpacity>
            </View>
          </View>
        </Animated.View>
      )}
      <UpgradeAccountSheet visible={upgradeOpen} onClose={() => setUpgradeOpen(false)} />
    </>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    position: 'absolute',
    left: SPACING.md,
    right: SPACING.md,
    alignItems: 'center',
    zIndex: 998,
  },
  toast: {
    width: '100%',
    maxWidth: 480,
    paddingHorizontal: SPACING.md,
    paddingVertical: SPACING.smd,
    borderRadius: BORDER_RADIUS.md,
    borderWidth: 1,
    gap: SPACING.sm,
    ...ELEVATION.floating,
  },
  message: {
    ...TYPOGRAPHY.label,
    lineHeight: 19,
  },
  actions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: SPACING.sm,
  },
  secondaryAction: {
    paddingHorizontal: SPACING.smd,
    paddingVertical: SPACING.xs,
  },
  secondaryActionText: {
    ...TYPOGRAPHY.label,
  },
  primaryAction: {
    paddingHorizontal: SPACING.md,
    paddingVertical: SPACING.xs,
    borderRadius: BORDER_RADIUS.full,
  },
  primaryActionText: {
    ...TYPOGRAPHY.label,
  },
});
